/**
 * POS (spec 05): grid catálogo | carrito, búsqueda con foco permanente, Enter agrega, F2 cliente,
 * F4 cobrar, F9 espera. Precios SIEMPRE del servidor (POST /ventas/calcular); triple precio por
 * línea y en totales. Lo que el usuario no puede hacer por sí mismo (descuento, crédito fuera de límite…) se hace
 * con autorización: el servidor la pide y el diálogo la obtiene con clave o a distancia.
 */
import { ARTICULO_PIEZAS_VIDRIO, ARTICULO_PRODUCTOS_DETALLE, articuloEspecialDe, formatearExacto, METODO_VUELTO_BS_ETIQUETA, Money, restanteEnDivisaBcv, type CalcularVentaInput, type CalculoComboDTO, type ComboDTO, type ParametrosCobroDTO, type CalculoVentaDTO, type ClienteDTO, type DocumentoDTO, type EsperaDTO, type PagoLineaInput, type PapelFiscalDTO, type PiezaDTO, type SesionCajaDTO, type ProductoResumenDTO, type VueltoEntrada, type TasaVigenteDTO } from '@mg/shared';
import { useQuery } from '@tanstack/react-query';
import { Layers, Lock, PauseCircle, Printer, Search, ShoppingCart, Trash2, UserPlus, Wallet } from 'lucide-react';
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { AyudaVentana } from '@/ayuda/BotonAyuda.tsx';
import { useAutorizacion } from '@/componentes/Autorizacion.tsx';
import { mostrarRegistrado } from '@/componentes/Registrado.tsx';
import { Dinero } from '@/componentes/Dinero.tsx';
import { PaginadorBusqueda, usePaginaBusqueda } from '@/componentes/PaginadorBusqueda.tsx';
import { ErrorEstado, RequiereSucursal, Vacio } from '@/componentes/estados.tsx';
import { Badge } from '@/componentes/ui/badge.tsx';
import { Button } from '@/componentes/ui/button.tsx';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/componentes/ui/dialog.tsx';
import { Input, Select } from '@/componentes/ui/input.tsx';
import { Campo } from '@/componentes/ui/label.tsx';
import { ErrorApi, get, mensajeDeError } from '@/lib/api.ts';
import { useAuth } from '@/lib/auth.tsx';
import { claves } from '@/lib/query.ts';
import { formatearFechaHora } from '@/lib/utils.ts';
import { BarraCaja, cajaDeOtroDia, DialogoAbrirCaja, FormAbrirCaja } from '@/modulos/caja/AbrirCaja.tsx';
import { AvisoRetiro } from '@/modulos/caja/Retiros.tsx';
import { DialogClienteRapido, EtiquetasCliente } from '@/modulos/clientes/Clientes.tsx';
import { useClientes } from '@/modulos/clientes/api.ts';
import { importarCotizacion } from '@/modulos/cotizaciones/api.ts';
import { combosParaEnviar, desarmarCombo, esPrimeroDeSuCombo, fotoDeCalculo, quitarCombo, reemplazarCombo, textoMedida, type ComboEnCarrito } from '@/modulos/combos/carrito.ts';
import { DialogAgregarCombo } from '@/modulos/combos/DialogAgregarCombo.tsx';
import { useProductos } from '@/modulos/productos/api.ts';
import { calcularVenta, useAbono, useDescartarEspera, useEmitirVenta, useEsperas, useGuardarEspera } from './api.ts';
import { AvisoTopeBsNotas } from './AvisoTopeBsNotas.tsx';
import { CobroDialog, type DetalleCobroUI } from './CobroDialog.tsx';
import { normalizarDecimal, parseMonto } from '@/lib/montos.ts';
import { veTasa2 } from '@/lib/tasa2.ts';

/** Renglón de la lista como lo escribe la dueña: «2 uni Vidrio claro 3mm 1000x2500», «3 uni Cuñete de pintura · tapa rota». */
const textoLote = (z: PiezaDTO) => `${z.disponible} uni ${z.nombre}${z.clase === 'detalle' && z.descripcion ? ` · ${z.descripcion}` : ''}`;

/** Lo que el carrito necesita del producto (una pieza de vidrio o un producto con detalle se arma con los datos de su lote). */
type ProductoCarrito = Pick<ProductoResumenDTO, 'id' | 'codigo' | 'nombre' | 'presentaciones'>;
interface LineaCarrito {
  producto: ProductoCarrito;
  presentacion_id: string;
  cantidad: string;
  /** Almacén del que sale el renglón; sin indicar = la tienda. Un depósito pide permiso o autorización. */
  almacen_id?: string;
  /** Pieza de vidrio o producto con detalle: un lote con su precio, por unidades enteras (hasta lo que queda). */
  pieza_id?: string;
  pieza?: { clase: PiezaDTO['clase']; disponible: string; detalle: string | null };
  /** Descuento manual % del renglón (viene de la cotización importada o de una espera): al emitir pide permiso o autorización. */
  descuento_pct?: string;
  /** Combo o receta del renglón (spec 20): mientras lo tenga lleva el descuento del combo y su cantidad no se edita suelta. */
  combo_grupo?: string;
}

const CF_RIF = 'V-00000000-0';
/** Productos por página en el buscador del POS. */
const LIMITE_POS = 12;
const nuevaClave = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random()}`);

/**
 * ¿El documento que devolvió el servidor es la venta que se envió? La clave de la venta se conserva entre reintentos:
 * si un intento anterior sí quedó emitido y su respuesta se perdió, el servidor devuelve aquel documento aunque el
 * carrito haya cambiado después.
 */
const firmaDeLineas = (ls: { producto_id: string; presentacion_id: string; cantidad: string }[]) => ls.map((l) => `${l.producto_id}:${l.presentacion_id}:${Money.parse(l.cantidad).toDocumento()}`).toSorted().join('|');
function esLaVentaEnviada(doc: DocumentoDTO, enviada: CalcularVentaInput): boolean {
  return doc.cliente_id === enviada.cliente_id && doc.tipo === enviada.tipo && firmaDeLineas(doc.lineas) === firmaDeLineas(enviada.lineas);
}

export function POS() {
  const { usuario, puede } = useAuth();
  const navigate = useNavigate();
  const buscador = useRef<HTMLInputElement>(null);
  /** Lista de productos con scroll: al pasar de página vuelve arriba. */
  const listaProductos = useRef<HTMLDivElement>(null);
  const clienteRef = useRef<HTMLInputElement>(null);
  const [q, setQ] = useState('');
  const [qCliente, setQCliente] = useState('');
  // F2 / foco en el buscador de cliente: la lista se despliega sin escribir (observación del cliente 24-09-2026)
  const [listaClientes, setListaClientes] = useState(false);
  const [clienteElegido, setCliente] = useState<ClienteDTO | null>(null);
  const [tipo, setTipo] = useState<'FAC' | 'NOTA'>('FAC');
  // Caja del cajero (bloque 8): estado del límite de efectivo (sin montos) y modalidad
  const sesionCaja = useQuery({ queryKey: claves.sesionCajaActual, queryFn: () => get<SesionCajaDTO | null>('/caja/sesiones/actual'), enabled: !!usuario?.sesion_caja, refetchInterval: 120_000 });
  // Tasas del día: el precio de lista se muestra en divisa y a BCV antes de armar el carrito
  const tasaDia = useQuery({ queryKey: claves.tasaVigente, queryFn: () => get<TasaVigenteDTO>('/tasas/vigente'), refetchInterval: 30_000 });
  // % de IGTF (la misma consulta que usa el cobro): el total «todo en dólares» de la factura lo incluye
  const paramsCobro = useQuery({ queryKey: ['ventas', 'parametros-cobro'], queryFn: () => get<ParametrosCobroDTO>('/ventas/parametros-cobro'), staleTime: 60_000 });
  /** Cambia cuando la dueña registra otra tasa: el carrito abierto se recalcula solo (H-03). */
  const tasaFirma = tasaDia.data ? `${tasaDia.data.bcv}|${tasaDia.data.paralela}` : '';
  const tasaVista = useRef('');
  /** Lo que hace F4, siempre con los datos del último render (se asigna más abajo, cuando ya se sabe si la venta está lista). */
  const cobrarConF4 = useRef<() => void>(() => undefined);
  /** Clave por referencia: reintentar el mismo anticipo tras un fallo de red no lo registra dos veces. */
  const clavesAnticipo = useRef(new Map<string, string>());
  const anticiparPago = async (pago: { metodo: 'zelle' | 'usdt'; monto: string; referencia: string }): Promise<string | null> => {
    if (!cliente) return null;
    const firma = `${cliente.id}|${pago.metodo}|${pago.referencia}`;
    const clave = clavesAnticipo.current.get(firma) ?? nuevaClave();
    clavesAnticipo.current.set(firma, clave);
    try {
      const r = await abono.mutateAsync({ datos: { cliente_id: cliente.id, pagos: [{ metodo: pago.metodo, monto: pago.monto, referencia: pago.referencia }] }, clave });
      toast.success(`Anticipo ${r.numero_formateado} registrado`, { description: `${cliente.razon_social} · saldo a favor $ ${Money.parse(r.saldo_a_favor).toDisplay()}. El recibo queda en la ficha del cliente.`, duration: 8000 });
      // El cliente y el cálculo se releen: el saldo a favor nuevo ya cuenta para esta venta
      get<ClienteDTO>(`/clientes/${cliente.id}`).then(setCliente).catch(() => undefined);
      return r.saldo_a_favor;
    } catch (e) {
      toast.error('No se pudo registrar el anticipo', { description: mensajeDeError(e) });
      return null;
    }
  };
  // Talonario de papel fiscal: se avisa antes de cobrar, no cuando el cliente ya pagó (H-04). Las hojas las gastan
  // también las otras cajas de la sucursal y las hojas en blanco que anula el administrador: se relee cada 20 s,
  // al entrar a la pantalla y después de cada venta propia. Agotado, cada 5 s: en cuanto la dueña carga el talonario
  // nuevo, la caja vuelve a facturar sin recargar
  const papel = useQuery({ queryKey: ['ventas', 'papel-fiscal'], queryFn: () => get<PapelFiscalDTO>('/ventas/papel-fiscal'), enabled: !!usuario?.sucursal_nombre, refetchInterval: (q) => (q.state.data?.con_talonario && q.state.data.restantes === 0 ? 5_000 : 20_000), staleTime: 0 });
  const hojas = papel.data?.con_talonario ? papel.data.restantes : null;
  const tasasRef = tasaDia.data ? { tasa_bcv: tasaDia.data.bcv, tasa_paralela: tasaDia.data.paralela } : undefined;
  /** Caja solo fiscal: emite facturas, no notas de entrega. */
  const soloFiscal = usuario?.sesion_caja?.modalidad === 'solo_fiscal';
  const [condicion, setCondicion] = useState<'contado' | 'credito'>('contado');
  const [modalidad, setModalidad] = useState<'base25' | 'completo' | ''>('');
  const [descuento, setDescuento] = useState('');
  const [carrito, setCarrito] = useState<LineaCarrito[]>([]);
  /** Combos y recetas del carrito (spec 20). */
  const [combos, setCombos] = useState<ComboEnCarrito[]>([]);
  /** Ventana «Agregar combo o receta» (`grupo` = «Cambiar medida» de uno que ya está). */
  const [dialogoCombo, setDialogoCombo] = useState<{ grupo: string | null; inicial: Parameters<typeof DialogAgregarCombo>[0]['inicial'] } | null>(null);
  /** Confirmación de desarmar un combo (`linea`: el renglón que se quita al desarmar; null = solo desarmar). */
  const [desarme, setDesarme] = useState<{ grupo: string; nombre: string; linea: number | null } | null>(null);
  const [calculo, setCalculo] = useState<CalculoVentaDTO | null>(null);
  const [errorCalculo, setErrorCalculo] = useState<string | null>(null);
  /** La entrada y la tasa del último cálculo que terminó (bien o con error): si no son las de ahora, el nuevo va en camino. */
  const [calculadoPara, setCalculadoPara] = useState<{ entrada: CalcularVentaInput; tasaFirma: string } | null>(null);
  /** F4 pulsado con el cálculo en camino: el cobro se abre cuando llegue el de esta entrada. */
  const [f4Para, setF4Para] = useState<CalcularVentaInput | null>(null);
  const [cobrando, setCobrando] = useState(false);
  // Clave de idempotencia de la venta en curso: se conserva en los reintentos (si el servidor emitió y la respuesta se
  // perdió, el segundo clic devuelve el mismo documento) y solo se renueva cuando la venta se emite o empieza otra
  const [claveIdem, setClaveIdem] = useState(nuevaClave);
  const [claveAbono, setClaveAbono] = useState(nuevaClave);
  const [emitido, setEmitido] = useState<DocumentoDTO | null>(null);
  /** El documento mostrado salió de un intento anterior y no coincide con el carrito actual (que sigue sin emitir). */
  const [emitidoAntes, setEmitidoAntes] = useState(false);
  const [nuevoCliente, setNuevoCliente] = useState(false);
  const [abrirCaja, setAbrirCaja] = useState(false);
  /**
   * Antes de cobrar: sin caja abierta no se cobra y se ofrece abrirla ahí mismo (el carrito sigue en pantalla); con la
   * caja abierta de otro día tampoco, hasta cerrarla. Antes el servidor lo decía recién al confirmar el cobro.
   */
  const cajaLista = (): boolean => {
    const otra = usuario?.caja_en_otra_sucursal;
    if (!usuario?.sesion_caja && otra) {
      toast.error(`Tu caja abierta es la ${otra.caja_nombre} de ${otra.sucursal_nombre}`, { description: 'Ciérrala allá para poder cobrar aquí.' });
      return false;
    }
    if (!usuario?.sesion_caja) {
      toast.info('Abre tu caja para cobrar', { description: 'Lo que tienes en el carrito no se pierde.' });
      setAbrirCaja(true);
      return false;
    }
    if (cajaDeOtroDia(usuario.sesion_caja.abierta_en)) {
      toast.error('Tu caja quedó abierta de otro día', { description: 'Cuéntala y ciérrala antes de vender hoy.' });
      return false;
    }
    return true;
  };
  const [esperaAbierta, setEsperaAbierta] = useState(false);
  const [nombreEspera, setNombreEspera] = useState('');
  const [esperaId, setEsperaId] = useState<string | null>(null);
  const [verEsperas, setVerEsperas] = useState(false);
  const [abonoAbierto, setAbonoAbierto] = useState(false);
  const [overrideMotivo, setOverrideMotivo] = useState('');
  const [cotizacion, setCotizacion] = useState<{ id: string; numero: string; conDescuento: boolean } | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const cotizacionParam = searchParams.get('cotizacion');

  const puedeDescontar = puede('ventas.descuento');
  const puedeCredito = puede('clientes.credito');
  const aut = useAutorizacion();
  const cf = useQuery({ queryKey: ['clientes', 'consumidor-final'], queryFn: () => get<{ datos: ClienteDTO[] }>('/clientes', { q: CF_RIF, limite: 1 }) });
  // Cliente por defecto: Consumidor Final (derivado, sin efecto)
  const cliente = clienteElegido ?? cf.data?.datos[0] ?? null;
  const listaId = cliente?.lista_id;
  // De 12 en 12 con paginador al pie (pedido de la dueña 2026-10-20); otra búsqueda u otra lista de precios vuelve a la 1
  const [paginaProd, setPaginaProd] = usePaginaBusqueda(`${q}|${listaId ?? ''}`);
  const productos = useProductos({ q: q || undefined, lista_id: listaId, pagina: paginaProd, limite: LIMITE_POS, activo: 'true' });
  // «PIEZAS DE VIDRIOS» y «PRODUCTOS CON DETALLES» (su «opción 2»): al buscarlos se despliega la lista completa de lo
  // disponible. Otra búsqueda también encuentra en esas listas lo que coincide por nombre. Sin búsqueda no se listan.
  const articulo = articuloEspecialDe(q);
  const buscaPiezas = !!articulo || q.trim().length >= 2;
  const piezas = useQuery({
    queryKey: ['inventario', 'piezas', 'disponible', articulo ?? q],
    queryFn: () => get<PiezaDTO[]>('/inventario/piezas', articulo ? { estado: 'disponible', clase: articulo } : { estado: 'disponible', q }),
    enabled: buscaPiezas,
    staleTime: 15_000,
  });
  const lotesVidrio = (piezas.data ?? []).filter((z) => z.clase === 'vidrio');
  const lotesDetalle = (piezas.data ?? []).filter((z) => z.clase === 'detalle');
  const hayLotes = buscaPiezas && (piezas.data?.length ?? 0) > 0;
  const clientes = useClientes({ q: qCliente || undefined, limite: 8 }, qCliente.length > 0 || listaClientes || !cliente);
  const esperas = useEsperas(puede('ventas.espera'));
  const emitir = useEmitirVenta();
  const guardarEspera = useGuardarEspera();
  const descartarEspera = useDescartarEspera();
  const abono = useAbono();

  const entrada = useMemo<CalcularVentaInput | null>(() => {
    if (!cliente || carrito.length === 0) return null;
    const enviarCombos = combosParaEnviar(combos, carrito);
    return {
      tipo,
      cliente_id: cliente.id,
      condicion,
      lineas: carrito.map((l) => ({
        producto_id: l.producto.id,
        presentacion_id: l.presentacion_id,
        cantidad: normalizarDecimal(l.cantidad) || '1',
        ...(l.almacen_id ? { almacen_id: l.almacen_id } : {}),
        ...(l.pieza_id ? { pieza_id: l.pieza_id } : {}),
        ...(l.descuento_pct && !l.pieza_id ? { descuento_pct: l.descuento_pct } : {}),
        ...(l.combo_grupo ? { combo_grupo: l.combo_grupo } : {}),
      })),
      ...(enviarCombos.length > 0 ? { combos: enviarCombos } : {}),
      ...(descuento.trim() ? { descuento_pct: descuento.trim() } : {}),
      ...(modalidad ? { modalidad_retencion: modalidad } : {}),
    };
  }, [cliente, carrito, combos, tipo, condicion, descuento, modalidad]);

  // Cálculo en el servidor (debounce corto)
  useEffect(() => {
    let vivo = true;
    const t = setTimeout(() => {
      if (!entrada) {
        setCalculo(null);
        setErrorCalculo(null);
        return;
      }
      calcularVenta(entrada)
        .then((c) => {
          if (!vivo) return;
          setCalculo(c);
          setErrorCalculo(null);
          setCalculadoPara({ entrada, tasaFirma });
        })
        .catch((e) => {
          if (!vivo) return;
          setCalculo(null);
          setErrorCalculo(mensajeDeError(e));
          setCalculadoPara({ entrada, tasaFirma });
        });
    }, 150);
    return () => {
      vivo = false;
      clearTimeout(t);
    };
  }, [entrada, tasaFirma]);

  // La tasa cambió con una venta en pantalla: ya se recalculó arriba; se le dice al cajero
  useEffect(() => {
    if (!tasaFirma) return;
    if (tasaVista.current && tasaVista.current !== tasaFirma && carrito.length > 0) {
      toast.warning('La tasa cambió', { description: 'La venta en pantalla se recalculó con la tasa nueva. Revisa el total antes de cobrar.', duration: 15000 });
    }
    tasaVista.current = tasaFirma;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasaFirma]);

  const agregar = useCallback((p: ProductoResumenDTO, presentacionId?: string) => {
    const pres = presentacionId ?? p.presentaciones.find((x) => Money.parse(x.unidades).eq(1))?.id ?? p.presentaciones[0]?.id;
    if (!pres) {
      toast.error(`${p.codigo} no tiene presentaciones activas`);
      return;
    }
    setCarrito((c) => {
      const i = c.findIndex((l) => l.producto.id === p.id && l.presentacion_id === pres && !l.pieza_id && !l.combo_grupo);
      if (i >= 0) return c.map((l, j) => (j === i ? { ...l, cantidad: (parseMonto(l.cantidad) ?? Money.cero()).add(1).toString() } : l));
      return [...c, { producto: p, presentacion_id: pres, cantidad: '1' }];
    });
    setQ('');
    buscador.current?.focus();
  }, []);

  /** Una pieza de vidrio o un producto con detalle entra a su precio; si ya está, suma una unidad (hasta lo que queda). */
  const agregarPieza = useCallback((z: PiezaDTO) => {
    if (!z.presentacion_id) {
      toast.error(`${z.producto_codigo} no tiene presentaciones activas`);
      return;
    }
    setCarrito((c) => {
      const i = c.findIndex((l) => l.pieza_id === z.id);
      if (i >= 0) return c.map((l, j) => (j === i ? { ...l, cantidad: String(Math.min(Number(z.disponible), (Number(normalizarDecimal(l.cantidad)) || 0) + 1)) } : l));
      return [...c, { producto: { id: z.producto_id, codigo: z.codigo, nombre: z.nombre, presentaciones: [] }, presentacion_id: z.presentacion_id!, cantidad: '1', pieza_id: z.id, pieza: { clase: z.clase, disponible: z.disponible, detalle: z.clase === 'detalle' ? z.descripcion : null } }];
    });
    setQ('');
    buscador.current?.focus();
  }, []);

  /** Pone los renglones de un combo calculado, o cambia los de uno que ya está (otra medida: conserva el descuento). */
  const agregarCombo = useCallback(async (calc: CalculoComboDTO, grupoExistente: string | null) => {
    try {
      const ids = [...new Set(calc.renglones.map((r) => r.producto_id))];
      const fichas = await Promise.all(ids.map((pid) => get<ProductoResumenDTO>(`/productos/${pid}`)));
      const grupo = grupoExistente ?? nuevaClave();
      const nuevas: LineaCarrito[] = calc.renglones.map((r) => ({ producto: fichas.find((x) => x.id === r.producto_id)!, presentacion_id: r.presentacion_id, cantidad: Money.parse(r.cantidad).toString(), combo_grupo: grupo }));
      const foto = fotoDeCalculo(calc, grupo);
      setCarrito((c) => reemplazarCombo(c, grupo, nuevas));
      setCombos((k) => [...k.filter((x) => x.grupo !== grupo), foto]);
      setDialogoCombo(null);
      toast.success(grupoExistente ? `Medida cambiada: ${calc.nombre}` : `${calc.nombre} en el carrito`, { description: `${textoMedida(foto)} · ${nuevas.length} producto(s) con el descuento del combo` });
      buscador.current?.focus();
    } catch (e) {
      toast.error('No se pudo agregar el combo', { description: mensajeDeError(e) });
    }
  }, []);

  const limpiar = useCallback(() => {
    setCarrito([]);
    setCombos([]);
    setTipo('FAC');
    setDescuento('');
    setModalidad('');
    setCondicion('contado');
    setEsperaId(null);
    setCotizacion(null);
    setOverrideMotivo('');
    setClaveIdem(nuevaClave());
    setCalculo(null);
    aut.limpiar();
    buscador.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Atajos de teclado
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'F2') {
        e.preventDefault();
        clienteRef.current?.focus();
        setListaClientes(true);
      } else if (e.key === 'F4') {
        e.preventDefault();
        cobrarConF4.current();
      } else if (e.key === 'F9') {
        e.preventDefault();
        if (carrito.length > 0) setEsperaAbierta(true);
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculo, carrito.length, usuario?.sesion_caja?.id]);

  const porCobrar = calculo?.totales.por_cobrar ?? '0';
  const requiereModalidad = calculo?.requiere_modalidad ?? false;
  // Consumidor Final sirve para ver precios y cotizar: la factura y la nota van a un cliente identificado
  const soloCotiza = !!cliente?.consumidor_final;
  /** El carrito sigue llevando el descuento de la cotización importada (no se quitó a mano). */
  const cotizacionConDescuento = !!cotizacion?.conDescuento && (!!descuento.trim() || carrito.some((l) => !!l.descuento_pct));
  /** Sin hojas en el talonario no se factura (la nota de entrega no usa papel fiscal). */
  const sinHojas = tipo === 'FAC' && hojas === 0;
  /**
   * El cálculo que se muestra es el de lo que hay en pantalla. Recién agregado o cambiado algo, el nuevo tarda un
   * momento (debounce + servidor) y el anterior sigue a la vista: cobrar con él abría el cobro con el total viejo y
   * precargaba un monto viejo.
   */
  const calculoTerminado = !!entrada && calculadoPara?.entrada === entrada && calculadoPara.tasaFirma === tasaFirma;
  const listo = !!calculo && calculoTerminado && carrito.length > 0 && !errorCalculo && !(requiereModalidad && !modalidad) && !calculo.lineas.some((l) => l.falta) && !soloCotiza && !sinHojas;
  cobrarConF4.current = () => {
    // Con el cálculo en camino, F4 queda pendiente y el cobro se abre al llegar (con los totales nuevos)
    if (entrada && !calculoTerminado) return setF4Para(entrada);
    if (listo && !emitir.isPending && cajaLista()) setCobrando(true);
  };
  useEffect(() => {
    if (!f4Para) return;
    // Si después de F4 cambió algo más (o se vació el carrito), ese F4 ya no vale
    if (f4Para !== entrada) return setF4Para(null);
    if (!calculoTerminado) return;
    setF4Para(null);
    cobrarConF4.current();
  }, [f4Para, entrada, calculoTerminado]);

  const emitirCon = async (pagos: PagoLineaInput[], vuelto?: VueltoEntrada) => {
    if (!entrada) return;
    try {
      // Si el servidor pide autorización (descuento, crédito fuera de límite…), el diálogo la obtiene y se reintenta
      const doc = await aut.ejecutar(
        (autorizaciones) =>
          emitir.mutateAsync({
            datos: { ...entrada, pagos, ...(vuelto ? { vuelto } : {}), autorizaciones, ...(esperaId ? { espera_id: esperaId } : {}), ...(cotizacion ? { cotizacion_id: cotizacion.id } : {}), ...(condicion === 'credito' && overrideMotivo.trim() ? { override_credito: { motivo: overrideMotivo.trim() } } : {}) },
            clave: claveIdem,
          }),
        {
          motivo: overrideMotivo.trim(),
          detalle: { cliente: cliente?.razon_social ?? '', documento: tipo === 'FAC' ? 'Factura' : 'Nota', total: `$ ${Money.parse(porCobrar).toDisplay()}`, ...(descuento.trim() ? { descuento: `${descuento.trim()} %` } : {}), ...(calculo && !Money.parse(calculo.totales.descuento).esCero() ? { descuento_usd: `$ ${Money.parse(calculo.totales.descuento).toDisplay()}` } : {}) },
          // Un descuento se autoriza por lo que se descuenta; lo demás, por lo que vale la venta
          montoUsd: (t) => (t === 'descuento' ? (calculo?.totales.descuento ?? porCobrar) : porCobrar),
        },
      );
      if (!doc) return; // canceló la autorización: la venta sigue en pantalla
      setCobrando(false);
      setEmitido(doc);
      if (!esLaVentaEnviada(doc, entrada)) {
        // Un intento anterior sí se emitió (su respuesta se perdió) y el carrito cambió después: se muestra aquel
        // documento, el carrito queda en pantalla sin emitir y lo que se cobre ahora es otra venta (clave nueva)
        setEmitidoAntes(true);
        setClaveIdem(nuevaClave());
        toast.warning(`${doc.tipo} ${doc.numero_formateado} ya había quedado emitida en un intento anterior`, { description: 'El carrito que ves ahora NO se emitió: revisa ese documento y cobra solo lo que falte.', duration: 15000 });
        return;
      }
      setEmitidoAntes(false);
      toast.success(`${doc.tipo} ${doc.numero_formateado} emitida`, { description: doc.control_formateado ? `N° de control ${doc.control_formateado}` : 'Documento no fiscal' });
      limpiar();
    } catch (e) {
      // La espera ya se cobró en otra pestaña o en otra caja (auditoría 2026-10-11, H-01): se cierra el cobro, el aviso
      // se queda más tiempo y la lista de esperas se refresca. El carrito sigue en pantalla para que el cajero lo limpie
      if (e instanceof ErrorApi && e.codigo === 'espera_facturada') {
        setCobrando(false);
        void esperas.refetch();
        toast.error('Esta venta ya se cobró en otra pantalla', { description: mensajeDeError(e), duration: 20000 });
        return;
      }
      // El pago no alcanza: puede ser que la dueña cambió la tasa mientras se cobraba (H-03). Se relee la tasa; si
      // cambió, el carrito se recalcula solo y se le dice al cajero qué hacer en vez de repetir el mismo rechazo
      if (e instanceof ErrorApi && e.codigo === 'pago_insuficiente') {
        const antes = tasaVista.current;
        const t = (await tasaDia.refetch()).data;
        if (t && antes && `${t.bcv}|${t.paralela}` !== antes) {
          toast.warning('La tasa cambió mientras cobrabas', { description: 'El monto por cobrar ya está recalculado con la tasa nueva. Vuelve a poner el pago (botón «Resto») y confirma.', duration: 20000 });
          return;
        }
      }
      if (e instanceof ErrorApi && e.codigo === 'rango_control_agotado') void papel.refetch();
      // La clave NO se renueva: si el servidor emitió y la respuesta se perdió, reintentar devuelve ese mismo documento
      toast.error('No se pudo emitir', { description: mensajeDeError(e) });
    }
  };

  /** Quitar el último renglón deja el carrito vacío: lo que se agregue después es otra venta (clave nueva). */
  const quitarLinea = (i: number) => {
    const grupo = carrito[i]?.combo_grupo;
    if (grupo) {
      setDesarme({ grupo, nombre: combos.find((k) => k.grupo === grupo)?.nombre ?? 'el combo', linea: i });
      return;
    }
    if (carrito.length === 1) setClaveIdem(nuevaClave());
    setCarrito((c) => c.filter((_, j) => j !== i));
  };
  /** Desarma el combo (sus renglones quedan sueltos, sin su descuento) y, si se pidió, quita el renglón. */
  const confirmarDesarme = () => {
    if (!desarme) return;
    const sueltos = desarmarCombo(carrito, desarme.grupo);
    const resto = desarme.linea === null ? sueltos : sueltos.filter((_, j) => j !== desarme.linea);
    if (resto.length === 0) setClaveIdem(nuevaClave());
    setCarrito(resto);
    setCombos((k) => k.filter((x) => x.grupo !== desarme.grupo));
    setDesarme(null);
    toast.info('Combo desarmado', { description: 'Sus productos quedan sueltos, sin el descuento del combo.' });
  };
  const quitarComboEntero = (grupo: string) => {
    const resto = quitarCombo(carrito, grupo);
    if (resto.length === 0) setClaveIdem(nuevaClave());
    setCarrito(resto);
    setCombos((k) => k.filter((x) => x.grupo !== grupo));
  };
  const quitarDescuentos = () => {
    setDescuento('');
    setCarrito((c) => c.map(({ descuento_pct: _d, ...resto }) => resto));
  };

  // Spec 04 regla 7: /pos?cotizacion=<id> importa las líneas AL PRECIO Y TASA DEL DÍA avisando diferencias
  useEffect(() => {
    if (!cotizacionParam) return;
    let vivo = true;
    importarCotizacion(cotizacionParam)
      .then(async (imp) => {
        const cli = await get<ClienteDTO>(`/clientes/${imp.cliente_id}`);
        const importables = imp.lineas.filter((l) => l.estado === 'ok' || l.estado === 'disponible_insuficiente');
        const ids = [...new Set(importables.map((l) => l.producto_id))];
        const cargados = await Promise.all(ids.map((pid) => get<ProductoResumenDTO>(`/productos/${pid}`)));
        if (!vivo) return;
        setCliente(cli);
        setTipo('FAC');
        setCondicion('contado');
        setModalidad('');
        setEsperaId(null);
        // Los descuentos de la cotización van al carrito: el global al campo de descuento y el manual de cada renglón a
        // su línea (el programado se aplica solo). Al emitir rigen las reglas de siempre: permiso o autorización
        const descuentoGlobal = Money.parse(imp.descuento_pct);
        const conDescuento = descuentoGlobal.esPositivo() || importables.some((l) => l.descuento_manual);
        setDescuento(descuentoGlobal.esPositivo() ? descuentoGlobal.toString() : '');
        // Un combo con algún renglón que no se puede importar entra desarmado (sin su descuento)
        const gruposIncompletos = new Set(imp.lineas.filter((l) => !importables.includes(l) && l.combo_grupo).map((l) => l.combo_grupo));
        setCarrito(importables.map((l) => ({ producto: cargados.find((c) => c.id === l.producto_id)!, presentacion_id: l.presentacion_id, cantidad: l.cantidad, ...(l.descuento_manual ? { descuento_pct: l.descuento_pct } : {}), ...(l.combo_grupo && !gruposIncompletos.has(l.combo_grupo) ? { combo_grupo: l.combo_grupo } : {}) })).filter((l) => l.producto));
        setCombos(imp.combos.filter((k) => !gruposIncompletos.has(k.grupo)));
        setCotizacion({ id: imp.cotizacion_id, numero: imp.numero_formateado, conDescuento });
        setClaveIdem(nuevaClave());
        const omitidas = imp.lineas.filter((l) => l.estado === 'inactivo' || l.estado === 'sin_precio');
        const avisos = [
          ...imp.diferencias,
          ...(omitidas.length > 0 ? [`${omitidas.length} línea(s) omitida(s): sustitúyelas`] : []),
          ...(conDescuento ? [puedeDescontar ? 'Trae descuento: va cargado en el carrito' : 'Trae descuento: para facturarla así se pedirá autorización al cobrar'] : []),
        ];
        toast.info(`Cotización ${imp.numero_formateado} importada a precios y tasa de hoy`, { description: avisos.length > 0 ? avisos.join(' · ') : 'Sin diferencias con lo cotizado', duration: 8000 });
        setSearchParams({}, { replace: true });
      })
      .catch((err) => { if (vivo) toast.error('No se pudo importar la cotización', { description: mensajeDeError(err) }); });
    return () => { vivo = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cotizacionParam]);

  const retomarEspera = (e: EsperaDTO) => {
    const p = e.payload;
    setTipo(p.tipo);
    setCondicion(p.condicion ?? 'contado');
    setModalidad(p.modalidad_retencion ?? '');
    setDescuento(p.descuento_pct ?? '');
    setEsperaId(e.id);
    get<ClienteDTO>(`/clientes/${p.cliente_id}`).then(setCliente).catch(() => undefined);
    // Las líneas se rehidratan con la ficha actual de cada producto (precio y disponible de hoy)
    const ids = [...new Set(p.lineas.map((l) => l.producto_id))];
    const combosEspera = p.combos ?? [];
    Promise.all([Promise.all(ids.map((id) => get<ProductoResumenDTO>(`/productos/${id}`))), Promise.all(combosEspera.map((k) => get<ComboDTO>(`/combos/${k.combo_id}`)))])
      .then(([cargados, fichasCombo]) => {
        setCombos(combosEspera.map((k, i) => ({ grupo: k.grupo, combo_id: k.combo_id, combo_version: fichasCombo[i]!.version, codigo: fichasCombo[i]!.codigo, nombre: fichasCombo[i]!.nombre, tipo: fichasCombo[i]!.tipo, unidad_medida: fichasCombo[i]!.unidad_medida, medida: k.medida })));
        // Las piezas de vidrio no se guardan en espera (otra caja puede venderlas): se vuelven a agregar si siguen disponibles
        setCarrito(p.lineas.filter((l) => !l.pieza_id).map((l) => ({ producto: cargados.find((c) => c.id === l.producto_id)!, presentacion_id: l.presentacion_id, cantidad: l.cantidad, ...(l.almacen_id ? { almacen_id: l.almacen_id } : {}), ...(l.descuento_pct ? { descuento_pct: l.descuento_pct } : {}), ...(l.combo_grupo ? { combo_grupo: l.combo_grupo } : {}) })).filter((l) => l.producto));
        setClaveIdem(nuevaClave());
        setVerEsperas(false);
        toast.info(`Espera "${e.nombre}" retomada`, { description: 'Se recalcula con precios y tasa de hoy.' });
      })
      .catch((err) => toast.error('No se pudo retomar la espera', { description: mensajeDeError(err) }));
  };

  /**
   * Factura de contado: lo que paga el cliente todo en bolívares (a tasa 2, con su referencial a BCV) y todo en dólares
   * (al precio real a tasa BCV, con el IGTF del precio sin IVA), igual que «Cuánto paga según cómo pague» del cobro.
   */
  const formasDePago =
    calculo && tipo === 'FAC' && condicion === 'contado' && calculo.totales.divisa
      ? (() => {
          const t = calculo.totales;
          const d = t.divisa!;
          const pct = paramsCobro.data && Money.parse(paramsCobro.data.igtf_pct).esPositivo() ? paramsCobro.data.igtf_pct : null;
          // El IGTF sin redondear (3 % del precio sin IVA); lo que se cobra en dólares sí va a céntimos
          const exacto = restanteEnDivisaBcv(t.por_cobrar, pct, '1', { precio: d.precio_sin_iva, divisa: '0' });
          const bs = Money.parse(t.por_cobrar_bs);
          return { bs, referencial: bs.bsAUsd(d.tasa_bcv).redondear2(), dolares: exacto.redondear2(), igtf: exacto.sub(t.por_cobrar) };
        })()
      : null;

  // Superadmin en consola global (sin contexto): el POS opera una sucursal
  if (!usuario?.sucursal_id) return <RequiereSucursal />;
  if (usuario.rol === 'cajero' && !usuario.sesion_caja) {
    return (
      <div className="mx-auto mt-6 max-w-md space-y-4 rounded-2xl border bg-card p-6" data-testid="pos-sin-caja">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-amber-100 p-3 text-amber-800"><Wallet className="h-6 w-6" /></div>
          <div>
            <p className="text-lg font-semibold">Tu caja está cerrada</p>
            <p className="text-sm text-muted-foreground">Para vender hay que abrirla. Elige tu caja, cuenta el fondo y confirma: quedas listo para vender aquí mismo.</p>
          </div>
        </div>
        <FormAbrirCaja />
      </div>
    );
  }
  // Conteo declarado: la caja ya no vende; solo falta terminar su cierre
  if (usuario.sesion_caja?.declarado) {
    return <Vacio icono={<Lock className="h-6 w-6" />} titulo="Tu caja está en cierre" descripcion="Ya declaraste tu conteo: termina el cierre (o pide volver a contar) para seguir vendiendo." accion={<Button asChild data-testid="pos-caja-en-cierre"><Link to="/caja">Ir a Caja</Link></Button>} />;
  }

  return (
    <div className="grid h-[calc(100vh-7.5rem)] grid-cols-[1.15fr_1fr] grid-rows-[auto_minmax(0,1fr)] gap-x-4 gap-y-2">
      {/* ─── Caja: cómo está y qué sigue (se ve al entrar, no al cobrar) ─── */}
      <div className="col-span-2">
        <BarraCaja alAbrir={() => setAbrirCaja(true)} alIrACaja={() => navigate('/caja')} />
      </div>
      <DialogoAbrirCaja abierto={abrirCaja} cerrar={() => setAbrirCaja(false)} />
      {/* ─── Catálogo ─── */}
      <section className="flex min-h-0 flex-col gap-3">
        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              ref={buscador}
              autoFocus
              placeholder="Buscar producto (código, nombre, marca)… Enter agrega"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => {
                // Enter agrega el primer resultado SOLO cuando la búsqueda actual ya cargó (nunca un resultado viejo)
                if (e.key === 'Enter' && !productos.isError && !productos.isPlaceholderData && !productos.isFetching && productos.data?.datos[0]) agregar(productos.data.datos[0]);
              }}
              data-cargando={productos.isFetching || productos.isPlaceholderData ? 'true' : 'false'}
              className="h-11 pl-8 text-base"
              data-testid="pos-buscar"
            />
          </div>
          <Button type="button" size="sm" variant="outline" className="h-11" onClick={() => { setQ(ARTICULO_PIEZAS_VIDRIO); buscador.current?.focus(); }} title={`Lista de ${ARTICULO_PIEZAS_VIDRIO}`} data-testid="pos-articulo-vidrio">Piezas de vidrio</Button>
          <Button type="button" size="sm" variant="outline" className="h-11" onClick={() => { setQ(ARTICULO_PRODUCTOS_DETALLE); buscador.current?.focus(); }} title={`Lista de ${ARTICULO_PRODUCTOS_DETALLE}`} data-testid="pos-articulo-detalle">Con detalles</Button>
          <Button type="button" size="sm" variant="outline" className="h-11" onClick={() => setDialogoCombo({ grupo: null, inicial: null })} title="Agregar un combo o una receta (varios productos de una vez)" data-testid="pos-combos"><Layers className="h-4 w-4" /> Combo o receta</Button>
          <div className="hidden gap-1 text-[10px] text-muted-foreground 2xl:flex">
            <kbd className="rounded border px-1">F2</kbd> cliente <kbd className="rounded border px-1">F4</kbd> cobrar <kbd className="rounded border px-1">F9</kbd> espera
          </div>
        </div>
        {/* La lista lleva su scroll; el paginador queda fijo al pie de la tarjeta: siempre a la vista y sin tapar las piezas de vidrio */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border bg-card">
          <div ref={listaProductos} className="min-h-0 flex-1 overflow-y-auto" data-testid="pos-lista">
            {productos.isLoading ? (
              <p className="p-4 text-sm text-muted-foreground">Buscando…</p>
            ) : productos.isError ? (
              // Sin conexión o con el servidor caído no se dice «Sin resultados»: el producto existe, lo que falla es la consulta (FE-01)
              <div className="p-3" data-testid="pos-productos-error">
                <ErrorEstado error={productos.error} reintentar={() => void productos.refetch()} />
              </div>
            ) : (productos.data?.datos.length ?? 0) === 0 ? (
              hayLotes || articulo ? null : <p className="p-4 text-sm text-muted-foreground">Sin resultados.</p>
            ) : (
              <ul className="divide-y">
                {productos.data!.datos.map((p) => (
                  <li key={p.id} className="flex items-center gap-3 px-3 py-2 hover:bg-accent/40" data-testid={`pos-producto-${p.codigo}`}>
                    <div className="min-w-0 flex-1">
                      <p className="truncate font-medium">
                        {p.nombre} {p.fragil ? <Badge variant="critico">Frágil</Badge> : null}
                      </p>
                      <p className="text-[11px] text-muted-foreground">
                        {p.codigo} · {p.marca ?? ''} ·{' '}
                        {p.tipo === 'servicio' ? (
                          <Badge variant="neutro">Servicio</Badge>
                        ) : (
                          <>
                            disp. <b className="num">{p.disponible_sucursal ?? '—'}</b>
                            {/* Existencia por ubicación: tienda y depósitos de la sucursal */}
                            {p.stock_por_almacen && p.stock_por_almacen.length > 1 ? <span className="num"> ({p.stock_por_almacen.map((s) => `${s.almacen_nombre.replace(/^(Tienda|Depósito) /, '')} ${s.disponible}`).join(' · ')})</span> : null}
                          </>
                        )}
                      </p>
                    </div>
                    <div className="flex flex-wrap justify-end gap-1">
                      {p.presentaciones.map((pr) => {
                        const precio = pr.precios.find((x) => x.lista_id === listaId)?.precio_unitario_divisa ?? pr.precios[0]?.precio_unitario_divisa;
                        return (
                          <Button key={pr.id} size="sm" variant="outline" className="h-auto flex-col items-end gap-0.5 px-2 py-1 leading-tight" onClick={() => agregar(p, pr.id)} data-testid={`pos-agregar-${p.codigo}-${pr.unidades}`}>
                            <span className="text-[11px]">{pr.nombre} <span className="text-muted-foreground">c/u</span></span>
                            {/* Los dos precios, igual de visibles: en divisa y a tasa BCV */}
                            {precio ? <Dinero valor={precio} modo="divisas" referencial={tasasRef} className="text-xs" /> : <span className="num text-[11px] text-muted-foreground">sin precio</span>}
                          </Button>
                        );
                      })}
                    </div>
                  </li>
                ))}
              </ul>
            )}
            {hayLotes ? (
              <>
                {[
                  { clase: 'vidrio' as const, titulo: ARTICULO_PIEZAS_VIDRIO, lotes: lotesVidrio, testid: 'pos-piezas' },
                  { clase: 'detalle' as const, titulo: ARTICULO_PRODUCTOS_DETALLE, lotes: lotesDetalle, testid: 'pos-detalles' },
                ]
                  .filter((g) => g.lotes.length > 0)
                  .map((g) => (
                    <div key={g.clase} className="border-t" data-testid={g.testid}>
                      <p className="bg-muted/60 px-3 py-1 text-[11px] font-medium uppercase text-muted-foreground">{g.titulo} · se venden por unidad a su precio</p>
                      <ul className="divide-y">
                        {g.lotes.map((z) => {
                          const enCarrito = carrito.find((l) => l.pieza_id === z.id);
                          return (
                            <li key={z.id} className="flex items-center gap-3 px-3 py-1.5 hover:bg-accent/40" data-testid={`pos-lote-${z.codigo}`}>
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-medium">{textoLote(z)}</p>
                                <p className="text-[11px] text-muted-foreground">{z.clase === 'vidrio' ? z.codigo : `${z.producto_codigo} · precio normal ${z.precio_normal ? `$ ${Money.parse(z.precio_normal).toDisplay()}` : '—'}`} · {z.almacen_nombre}{z.clase === 'vidrio' && z.descripcion ? ` · ${z.descripcion}` : ''}</p>
                              </div>
                              <Button size="sm" variant="outline" className="h-8" disabled={!!enCarrito && Number(normalizarDecimal(enCarrito.cantidad)) >= Number(z.disponible)} onClick={() => agregarPieza(z)} data-testid={`pos-pieza-${z.codigo}`}>
                                $ {Money.parse(z.precio_divisa ?? '0').toDisplay()}
                              </Button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
              </>
            ) : articulo && piezas.data ? (
              <p className="border-t p-4 text-sm text-muted-foreground" data-testid="pos-lista-vacia">No hay nada en «{articulo === 'vidrio' ? ARTICULO_PIEZAS_VIDRIO : ARTICULO_PRODUCTOS_DETALLE}» ahora mismo.</p>
            ) : null}
          </div>
          {!productos.isError ? (
            <PaginadorBusqueda
              pagina={paginaProd}
              limite={LIMITE_POS}
              total={productos.data?.total ?? 0}
              onPagina={(n) => {
                setPaginaProd(n);
                if (listaProductos.current) listaProductos.current.scrollTop = 0;
              }}
              testid="pos-productos"
              grande
            />
          ) : null}
        </div>
      </section>

      {/* ─── Carrito ─── */}
      <section className="flex min-h-0 flex-col rounded-2xl border bg-card">
        {sesionCaja.data?.retiro?.excede ? <div className="p-2 pb-0"><AvisoRetiro retiro={sesionCaja.data.retiro} alRetirar={() => navigate('/caja')} /></div> : null}
        <div className="space-y-2 border-b p-3">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Input
                ref={clienteRef}
                placeholder="Cliente (F2): nombre o RIF…"
                value={qCliente}
                onChange={(e) => setQCliente(e.target.value)}
                onFocus={() => setListaClientes(true)}
                onBlur={() => setTimeout(() => setListaClientes(false), 150)}
                onKeyDown={(e) => { if (e.key === 'Escape') { setListaClientes(false); setQCliente(''); } }}
                className="h-9"
                data-testid="pos-cliente-buscar"
              />
              {(qCliente || listaClientes) && clientes.data && !clientes.isPlaceholderData && !clientes.isFetching ? (
                <ul className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-lg border bg-popover shadow-md" data-testid="pos-cliente-lista">
                  {clientes.data.datos.map((c) => (
                    <li key={c.id}>
                      <button
                        className="flex w-full flex-col px-3 py-1.5 text-left text-sm hover:bg-accent"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          setCliente(c);
                          setQCliente('');
                          setListaClientes(false);
                          setModalidad('');
                          setCondicion('contado');
                          buscador.current?.focus();
                        }}
                        data-testid={`pos-cliente-${c.rif}`}
                      >
                        <span className="font-medium">{c.razon_social}</span>
                        <span className="text-[11px] text-muted-foreground">{c.rif} · {c.lista_nombre}</span>
                      </button>
                    </li>
                  ))}
                  {clientes.data.datos.length === 0 ? <li className="px-3 py-2 text-xs text-muted-foreground">Sin coincidencias.</li> : null}
                </ul>
              ) : null}
            </div>
            <Button size="sm" variant="outline" onClick={() => setNuevoCliente(true)} data-testid="pos-nuevo-cliente">
              <UserPlus /> Nuevo
            </Button>
          </div>
          {cliente ? (
            <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="pos-cliente-actual">
              <span className="font-medium">{cliente.razon_social}</span>
              <span className="text-xs text-muted-foreground">{cliente.rif} · {cliente.lista_nombre}</span>
              <EtiquetasCliente c={cliente} />
              {Money.parse(cliente.saldo_a_favor).esPositivo() ? <Badge variant="ok">Saldo $ {Money.parse(cliente.saldo_a_favor).toDisplay()}</Badge> : null}
              {cliente.consumidor_final ? <Badge variant="pendiente" data-testid="pos-cf-solo-cotiza">Solo para cotizar</Badge> : tipo === 'FAC' && !cliente.apto_factura ? <Badge variant="critico">Sin datos fiscales para FAC</Badge> : null}
            </div>
          ) : null}
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex overflow-hidden rounded-lg border" role="radiogroup" aria-label="Tipo de documento">
              {(soloFiscal ? (['FAC'] as const) : (['FAC', 'NOTA'] as const)).map((t) => (
                <button key={t} role="radio" aria-checked={tipo === t} className={`px-3 py-1.5 text-sm font-medium ${tipo === t ? 'bg-mg-verde text-white' : 'bg-card'}`} onClick={() => setTipo(t)} data-testid={`pos-tipo-${t}`}>
                  {t === 'FAC' ? 'Factura' : 'Nota'}
                </button>
              ))}
            </div>
            {cliente?.condicion === 'credito' || puedeCredito ? (
              <Select value={condicion} onChange={(e) => setCondicion(e.target.value as 'contado' | 'credito')} className="h-9 w-32" aria-label="Condición" data-testid="pos-condicion">
                <option value="contado">Contado</option>
                <option value="credito">Crédito</option>
              </Select>
            ) : null}
            {requiereModalidad ? (
              <Select value={modalidad} onChange={(e) => setModalidad(e.target.value as 'base25' | 'completo' | '')} className={`h-9 w-56 ${modalidad ? '' : 'border-mg-ambar ring-2 ring-mg-ambar/40'}`} aria-label="Modalidad de retención" aria-invalid={!modalidad} data-testid="pos-modalidad">
                <option value="">Modalidad de retención…</option>
                <option value="base25">Base + 25% IVA (75% a crédito)</option>
                <option value="completo">Pago completo (reintegro después)</option>
              </Select>
            ) : null}
            <div className="ml-auto flex items-center gap-1">
              {!puedeDescontar ? (
                <span className="inline-flex items-center gap-1 text-xs text-muted-foreground" title="Precios fijos. Un descuento requiere autorización" data-testid="pos-candado">
                  <Lock className="h-3 w-3" /> precios fijos
                </span>
              ) : null}
              <Input inputMode="decimal" placeholder={puedeDescontar ? 'Desc. %' : 'Desc. % (autoriz.)'} title={puedeDescontar ? 'Descuento %' : 'El descuento requiere autorización de un administrador'} value={descuento} onChange={(e) => setDescuento(e.target.value)} className={puedeDescontar ? 'h-9 w-20' : 'h-9 w-32'} aria-label="Descuento %" data-testid="pos-descuento" />
            </div>
          </div>
        </div>

        {cotizacion ? (
          <div className="flex items-center justify-between gap-2 border-b bg-mg-verde-claro px-3 py-1.5 text-xs text-mg-verde-oscuro" data-testid="pos-cotizacion-banner">
            <span>
              Desde cotización <b>{cotizacion.numero}</b> · precios y tasa de HOY{cotizacionConDescuento ? <> · <b data-testid="pos-cotizacion-descuento">con su descuento</b></> : null} · quedará facturada al cobrar
            </span>
            <button className="underline" onClick={() => setCotizacion(null)}>desvincular</button>
          </div>
        ) : null}
        <div className="min-h-0 flex-1 overflow-y-auto">
          {carrito.length === 0 ? (
            <div className="flex h-full flex-col items-center justify-center gap-2 p-6 text-center text-sm text-muted-foreground">
              <ShoppingCart className="h-8 w-8" />
              Busca un producto y presiona Enter.
            </div>
          ) : (
            <table className="w-full text-[13px]">
              <thead className="sticky top-0 bg-muted/80 text-[11px] uppercase text-muted-foreground">
                <tr>
                  <th className="px-3 py-1.5 text-left">Producto</th>
                  <th className="px-2 py-1.5 text-right">Cant.</th>
                  <th className="px-2 py-1.5 text-right">Precio (Bs · $ a BCV · $)</th>
                  <th className="px-2 py-1.5 text-right">Subtotal (Bs · $ a BCV · $)</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {carrito.map((l, i) => {
                  const calc = calculo?.lineas[i];
                  const pres = l.producto.presentaciones.find((p) => p.id === l.presentacion_id);
                  const combo = l.combo_grupo ? combos.find((k) => k.grupo === l.combo_grupo) : undefined;
                  return (
                    <Fragment key={`${l.producto.id}-${l.presentacion_id}-${l.pieza_id ?? ''}-${l.combo_grupo ?? ''}`}>
                    {/* Cabecera del bloque del combo o receta (spec 20) */}
                    {esPrimeroDeSuCombo(carrito, i) ? (
                      <tr className="border-t bg-mg-verde-claro/60" data-testid={`pos-combo-${combo?.codigo ?? ''}`}>
                        <td colSpan={5} className="px-3 py-1.5">
                          <div className="flex flex-wrap items-center gap-2 text-xs">
                            <Layers className="h-4 w-4 text-mg-verde" />
                            <span className="font-semibold text-foreground">{combo?.nombre ?? 'Combo'}</span>
                            {combo ? <span className="text-muted-foreground" data-testid="pos-combo-medida">{textoMedida(combo)}</span> : null}
                            <span className="ml-auto flex flex-wrap gap-1">
                              {combo ? <Button type="button" size="sm" variant="ghost" className="h-7" onClick={() => setDialogoCombo({ grupo: combo.grupo, inicial: { combo_id: combo.combo_id, nombre: combo.nombre, tipo: combo.tipo, unidad_medida: combo.unidad_medida, medida: combo.medida } })} data-testid="pos-combo-cambiar">Cambiar medida</Button> : null}
                              <Button type="button" size="sm" variant="ghost" className="h-7" onClick={() => setDesarme({ grupo: l.combo_grupo!, nombre: combo?.nombre ?? 'el combo', linea: null })} data-testid="pos-combo-desarmar">Desarmar</Button>
                              <Button type="button" size="sm" variant="ghost" className="h-7 text-mg-rojo" onClick={() => quitarComboEntero(l.combo_grupo!)} data-testid="pos-combo-quitar">Quitar combo</Button>
                            </span>
                          </div>
                        </td>
                      </tr>
                    ) : null}
                    <tr className={`border-t ${calc?.falta ? 'bg-red-50' : l.combo_grupo ? 'bg-mg-verde-claro/20' : ''}`} data-testid={`pos-linea-${l.producto.codigo}`}>
                      <td className="px-3 py-1.5">
                        <p className="font-medium">{l.producto.nombre}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {l.pieza ? (
                            <>{l.pieza.clase === 'vidrio' ? 'Pieza de vidrio' : `Con detalle${l.pieza.detalle ? `: ${l.pieza.detalle}` : ''}`} · quedan {l.pieza.disponible}</>
                          ) : (
                            <>{pres?.nombre} × {pres ? Money.parse(pres.unidades).toDisplay() : ''} u</>
                          )}{' '}
                          {calc?.falta ? <span className="text-mg-rojo">· {l.pieza_id ? 'quedan' : 'disponible'} {calc.disponible}{calc.almacen_nombre ? ` en ${calc.almacen_nombre}` : ''}</span> : null}
                          {calc?.sin_inventario && !l.pieza_id ? <span>· servicio (sin inventario)</span> : null}
                        </p>
                        {/* Descuento que la dueña programó: se aplica solo */}
                        {calc?.descuento_programado ? <p className="text-[11px] font-medium text-mg-verde" data-testid={`pos-descuento-programado-${l.producto.codigo}`}>−{Money.parse(calc.descuento_programado.porcentaje).toDisplay()} % · {calc.descuento_programado.nombre}</p> : null}
                        {/* Descuento del combo: solo mientras el renglón esté en el combo (spec 20) */}
                        {calc?.descuento_combo ? <p className="text-[11px] font-medium text-mg-verde" data-testid={`pos-descuento-combo-${l.producto.codigo}`}>−{Money.parse(calc.descuento_combo).toDisplay()} % · descuento del combo</p> : null}
                        {/* Descuento manual del renglón (de la cotización o de la espera): pide permiso o autorización al cobrar */}
                        {calc && !calc.descuento_programado && !calc.descuento_combo && Money.parse(calc.descuento_pct).esPositivo() ? (
                          <p className="text-[11px] font-medium text-amber-800" data-testid={`pos-linea-descuento-${l.producto.codigo}`}>
                            −{Money.parse(calc.descuento_pct).toDisplay()} % de descuento{' '}
                            <button type="button" className="underline" onClick={() => setCarrito((c) => c.map((x, j) => { if (j !== i) return x; const { descuento_pct: _d, ...resto } = x; return resto; }))} data-testid={`pos-linea-quitar-descuento-${l.producto.codigo}`}>quitar</button>
                          </p>
                        ) : null}
                        {/* Venta contra depósito: el renglón sale del depósito elegido */}
                        {calc?.de_deposito ? (
                          <p className="text-[11px] font-medium text-amber-800" data-testid={`pos-linea-almacen-${l.producto.codigo}`}>
                            Sale de {calc.almacen_nombre}{' '}
                            <button type="button" className="underline" onClick={() => setCarrito((c) => c.map((x, j) => { if (j !== i) return x; const { almacen_id: _a, ...resto } = x; return resto; }))}>volver a la tienda</button>
                          </p>
                        ) : null}
                        {calc?.falta && calc.alternativas.length > 0 ? (
                          <p className="mt-0.5 flex flex-wrap items-center gap-1 text-[11px]">
                            <span className="text-muted-foreground">Vender desde:</span>
                            {calc.alternativas.map((a) => (
                              <button key={a.almacen_id} type="button" className="rounded border border-mg-ambar bg-amber-50 px-1.5 py-0.5 font-medium text-amber-900 hover:bg-amber-100" onClick={() => setCarrito((c) => c.map((x, j) => (j === i ? { ...x, almacen_id: a.almacen_id } : x)))} data-testid={`pos-deposito-${l.producto.codigo}`}>
                                {a.almacen_nombre} ({a.disponible})
                              </button>
                            ))}
                          </p>
                        ) : null}
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        {l.combo_grupo ? (
                          <span className="inline-flex items-center gap-1 num" title="Para cambiar la cantidad, cambia la medida del combo o desármalo" data-testid={`pos-cantidad-${l.producto.codigo}`}>
                            <Lock className="h-3 w-3 text-muted-foreground" />
                            {Money.parse(normalizarDecimal(l.cantidad) || '0').toDisplay()}
                          </span>
                        ) : (
                        <Input inputMode={l.pieza_id ? 'numeric' : 'decimal'} value={l.cantidad} onChange={(e) => setCarrito((c) => c.map((x, j) => (j === i ? { ...x, cantidad: e.target.value } : x)))} className="h-8 w-16 text-right" aria-label={`Cantidad ${l.producto.codigo}`} data-testid={`pos-cantidad-${l.producto.codigo}`} />
                        )}
                      </td>
                      {/* Bs exactos de la línea (precio × tasa 2 redondeado), los mismos del papel */}
                      <td className="px-2 py-1.5 text-right">{calc ? <Dinero valor={calc.precio_unitario} modo="triple" doc={{ tasa_bcv: calculo!.tasas.tasa_bcv, tasa_paralela: calculo!.tasas.tasa_paralela }} bs={calc.precio_unitario_bs} /> : '…'}</td>
                      <td className="px-2 py-1.5 text-right">{calc ? <Dinero valor={calc.subtotal} modo="triple" doc={{ tasa_bcv: calculo!.tasas.tasa_bcv, tasa_paralela: calculo!.tasas.tasa_paralela }} bs={calc.subtotal_bs} /> : '…'}</td>
                      <td className="px-1">
                        <Button size="icon" variant="ghost" onClick={() => quitarLinea(i)} aria-label="Quitar">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="space-y-2 border-t p-3" data-testid="pos-totales">
          {errorCalculo ? <p className="rounded-lg bg-red-50 px-3 py-1.5 text-xs text-red-800" data-testid="pos-error-calculo">{errorCalculo}</p> : null}
          {tipo === 'FAC' && hojas === 0 ? (
            <p className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm font-medium text-red-900" role="alert" data-testid="pos-aviso-talonario">
              Se agotó el talonario de facturas ({papel.data?.desde} a {papel.data?.hasta}). No se puede facturar hasta que la dueña cargue el talonario nuevo en Configuración. La nota de entrega sí sale.
            </p>
          ) : tipo === 'FAC' && hojas !== null && hojas <= 20 ? (
            <p className="rounded-lg border border-mg-ambar bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-900" role="status" data-testid="pos-aviso-talonario">
              {hojas === 1 ? 'Queda 1 hoja' : `Quedan ${hojas} hojas`} del talonario de facturas. Avisa a la dueña para que cargue el talonario nuevo antes de que se acabe.
            </p>
          ) : null}
          {requiereModalidad && !modalidad ? (
            <p className="rounded-lg border border-mg-ambar bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-900" data-testid="pos-aviso-modalidad">
              {cliente?.razon_social} es contribuyente especial o municipal: elige arriba la modalidad de retención para poder cobrar.
            </p>
          ) : null}
          {calculo?.advertencias.map((a) => (
            <p key={a} className="rounded-lg bg-amber-50 px-3 py-1.5 text-xs text-amber-900">{a}</p>
          ))}
          {calculo ? (
            <div className="grid grid-cols-3 gap-2 text-sm">
              <div className="col-span-2 space-y-0.5 text-xs text-muted-foreground">
                <p>Base <b className="num text-foreground">Bs {Money.parse(calculo.totales.base_bs).toDisplay()}</b> <span className="num">($ {Money.parse(calculo.totales.base).toDisplay()})</span>{Money.parse(calculo.totales.descuento).esPositivo() ? <> · desc. Bs {Money.parse(calculo.totales.descuento_bs).toDisplay()}</> : null}</p>
                <p>IVA {calculo.totales.iva_pct}% <b className="num text-foreground">Bs {Money.parse(calculo.totales.iva_bs).toDisplay()}</b> <span className="num">($ {Money.parse(calculo.totales.iva).toDisplay()})</span> {tipo === 'NOTA' ? <span className="text-[10px]">(nota: sin IVA)</span> : null}</p>
                {Money.parse(calculo.totales.retencion_iva).esPositivo() || Money.parse(calculo.totales.retencion_municipal).esPositivo() ? (
                  <p>
                    Retención IVA 75% <b className="num">$ {Money.parse(calculo.totales.retencion_iva).toDisplay()}</b>
                    {Money.parse(calculo.totales.retencion_municipal).esPositivo() ? <> · municipal {Money.parse(calculo.totales.retencion_municipal_pct).toString()}% <b className="num">$ {Money.parse(calculo.totales.retencion_municipal).toDisplay()}</b></> : null}
                    {modalidad === 'base25' ? ' (descontadas al cobrar)' : modalidad === 'completo' ? ' (paga todo)' : ''}
                  </p>
                ) : null}
              </div>
              {formasDePago ? (
                <div className="space-y-1.5 text-right" data-testid="pos-total-formas">
                  <div data-testid="pos-total-bs">
                    <p className="text-xs text-muted-foreground">Todo en bolívares</p>
                    <p className="num text-base font-bold">Bs {formasDePago.bs.toDisplay()}</p>
                    {/* El referencial a BCV junto al precio en dólares deja ver la regla: solo la dueña (pedido de la dueña 2026-10-19) */}
                    {veTasa2(usuario) ? <p className="num text-xs text-mg-azul" data-testid="pos-total-referencial">referencial $ {formasDePago.referencial.toDisplay()} a BCV</p> : null}
                  </div>
                  <div data-testid="pos-total-divisa">
                    <p className="text-xs text-muted-foreground">Todo en dólares</p>
                    <p className="num text-base font-bold text-mg-verde-oscuro">$ {formasDePago.dolares.toDisplay()}</p>
                    {formasDePago.igtf.esPositivo() ? <p className="num text-xs text-muted-foreground">con IGTF $ {formatearExacto(formasDePago.igtf)}</p> : null}
                  </div>
                </div>
              ) : (
                <div className="text-right">
                  <p className="text-xs text-muted-foreground">Total</p>
                  <Dinero valor={calculo.totales.total} modo="triple" doc={{ tasa_bcv: calculo.tasas.tasa_bcv, tasa_paralela: calculo.tasas.tasa_paralela }} bs={calculo.totales.total_bs} className="text-base" />
                </div>
              )}
            </div>
          ) : null}
          {calculo && Money.parse(calculo.cliente.vencido_usd).esPositivo() ? (
            <p className="rounded-lg border border-red-300 bg-red-50 px-3 py-1.5 text-xs font-medium text-red-900" role="status" data-testid="pos-aviso-vencido">
              {calculo.cliente.razon_social} tiene $ {Money.parse(calculo.cliente.vencido_usd).toDisplay()} vencidos.{' '}
              {calculo.autorizaciones_requeridas.includes('cliente_vencido') ? 'Para venderle (de contado o a crédito) se pedirá autorización al cobrar.' : 'Tu usuario puede venderle; la dueña recibirá el aviso.'}
            </p>
          ) : null}
          {/* Tope de Bs en notas lleno: se avisa antes de cobrar (pedido de la dueña 19-10-2026). Solo pantalla */}
          {calculo && tipo === 'NOTA' && condicion === 'contado' ? <AvisoTopeBsNotas bsNotas={calculo.bs_notas} queSeCobra="Esta nota" testid="pos-aviso-tope-bs" /> : null}
          {calculo && tipo === 'NOTA' && !calculo.cliente.apto_nota ? (
            <p className="rounded-lg border border-mg-ambar bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-900" role="status" data-testid="pos-aviso-telefono">
              La nota de entrega pide el teléfono del cliente: complétalo en su ficha (Clientes) antes de cobrar.
            </p>
          ) : null}
          {soloCotiza && carrito.length > 0 ? (
            <p className="rounded-lg border border-mg-ambar bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-900" role="status" data-testid="pos-aviso-consumidor-final">Consumidor Final es solo para cotizar: elige o registra al cliente (F2) para cobrar la factura o la nota.</p>
          ) : null}
          {calculo?.autorizaciones_requeridas.includes('venta_deposito') ? (
            <p className="rounded-lg bg-amber-50 px-3 py-1.5 text-xs text-amber-900" data-testid="pos-aviso-deposito">Vender contra un depósito requiere autorización: se pedirá al cobrar (clave de un administrador o a distancia).</p>
          ) : null}
          {calculo?.autorizaciones_requeridas.includes('descuento') ? (
            cotizacion && cotizacionConDescuento ? (
              <p className="rounded-lg border border-mg-ambar bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-900" role="status" data-testid="pos-aviso-descuento-cotizacion">
                La cotización {cotizacion.numero} trae descuento (Bs {Money.parse(calculo.totales.descuento_bs).toDisplay()} · $ {Money.parse(calculo.totales.descuento).toDisplay()}). Tu usuario no puede dar descuentos: para facturarla así se pedirá autorización al cobrar (clave de un administrador o a distancia).{' '}
                <button type="button" className="underline" onClick={quitarDescuentos} data-testid="pos-quitar-descuentos">Quitar el descuento y facturar a precio completo</button>
              </p>
            ) : (
              <p className="rounded-lg bg-amber-50 px-3 py-1.5 text-xs text-amber-900" data-testid="pos-aviso-autorizacion">El descuento requiere autorización: se pedirá al cobrar (clave de un administrador o a distancia).</p>
            )
          ) : null}
          {condicion === 'credito' ? (
            <Input placeholder={puedeCredito ? 'Motivo para vender sobre el límite de crédito (solo si lo excede)' : 'Motivo para vender sobre el límite (requiere autorización de la dueña)'} value={overrideMotivo} onChange={(e) => setOverrideMotivo(e.target.value)} className="h-8 text-xs" data-testid="pos-override" />
          ) : null}
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setVerEsperas(true)} data-testid="pos-ver-esperas">
              <PauseCircle /> Esperas {esperas.data?.length ? <Badge variant="pendiente">{esperas.data.length}</Badge> : null}
            </Button>
            <Button variant="outline" size="sm" onClick={() => setEsperaAbierta(true)} disabled={carrito.length === 0} data-testid="pos-espera">
              F9 · Espera
            </Button>
            <Button variant="outline" size="sm" onClick={() => { if (!cajaLista()) return; setClaveAbono(nuevaClave()); setAbonoAbierto(true); }} disabled={!cliente || cliente.consumidor_final} data-testid="pos-abono">
              Abono
            </Button>
            <Button variant="ghost" size="sm" onClick={limpiar} disabled={carrito.length === 0}>
              Limpiar
            </Button>
            <Button className="ml-auto h-11 px-6 text-base" disabled={!listo || emitir.isPending} onClick={() => { if (condicion === 'credito') void emitirCon([]); else if (cajaLista()) setCobrando(true); }} cargando={emitir.isPending} data-testid="pos-cobrar">
              {condicion === 'credito' ? 'Emitir a crédito' : `F4 · Cobrar $ ${Money.parse(porCobrar).toDisplay()}${calculo ? ` · Bs ${Money.parse(calculo.totales.por_cobrar_bs).toDisplay()}` : ''}`}
            </Button>
          </div>
        </div>
      </section>

      {cobrando && calculo && cliente ? (
        <CobroDialog
          abierto={cobrando}
          cerrar={() => setCobrando(false)}
          ctx={{
            titulo: `Cobrar ${tipo === 'FAC' ? 'factura' : 'nota'} · ${cliente.razon_social}`,
            porCobrar: calculo.totales.por_cobrar,
            tasaBs: calculo.totales.tasa_aplicada,
            tasaBcv: calculo.tasas.tasa_bcv,
            // Los Bs cubren EXACTO lo que dice el papel (Bs calculados por línea): por_cobrar_bs ÷ por_cobrar
            tasaBsCobro: Money.parse(calculo.totales.por_cobrar).esPositivo() ? Money.parse(calculo.totales.por_cobrar_bs).div(calculo.totales.por_cobrar).toString() : null,
            factorDivisa: '1',
            // Factura de contado: el precio en Bs depende de lo que entregue en divisas (regla de la dueña del 2026-10-15)
            precioDivisa:
              tipo === 'FAC' && condicion === 'contado' && calculo.totales.divisa
                ? { precioSinIva: calculo.totales.divisa.precio_sin_iva, tope: calculo.totales.divisa.tope_resto, tasaBcv: calculo.totales.divisa.tasa_bcv, tasa2: calculo.tasas.tasa_paralela, porCobrarBs: calculo.totales.divisa.por_cobrar_bs }
                : null,
            // Lo que se vende, con sus totales en $ y en Bs (a tasa 2 y, en la factura de contado, al precio real a tasa BCV)
            detalle: detalleCobro(calculo, tipo === 'FAC', tipo === 'FAC' && condicion === 'contado'),
            topeBs: calculo.totales.tope_bs,
            bsNotas: calculo.bs_notas,
            aplicaIgtf: tipo === 'FAC',
            saldoFavor: calculo.cliente.saldo_a_favor,
            // Con el cliente, el cobro sabe qué parte de su saldo entró en divisas sin IGTF (pedido de la dueña 2026-10-19)
            clienteId: calculo.cliente.id,
            // Zelle o Binance mayor que la venta: se registra completo como anticipo y se paga con el saldo (H-07)
            anticipo: cliente.consumidor_final
              ? { motivo: 'Consumidor final no acumula saldo a favor: pon al cliente con su cédula o RIF para registrar este pago como anticipo.' }
              : { registrar: anticiparPago },
          }}
          alCobrar={(pagos, vuelto) => void emitirCon(pagos, vuelto)}
          cargando={emitir.isPending || !calculoTerminado}
          ayuda={<AyudaVentana tema="pos.cobro" />}
        />
      ) : null}

      {aut.dialogo}
      <DialogClienteRapido abierto={nuevoCliente} cerrar={() => setNuevoCliente(false)} alCrear={(c) => { setCliente(c); setQCliente(''); buscador.current?.focus(); }} />

      {/* Combos y recetas (spec 20): agregar o cambiar la medida, y confirmar antes de desarmar */}
      <DialogAgregarCombo abierto={!!dialogoCombo} cerrar={() => setDialogoCombo(null)} inicial={dialogoCombo?.inicial ?? null} alAgregar={(c) => void agregarCombo(c, dialogoCombo?.grupo ?? null)} />
      <Dialog open={!!desarme} onOpenChange={(o) => !o && setDesarme(null)}>
        <DialogContent className="max-w-md" data-testid="dialogo-desarmar-combo">
          <DialogHeader>
            <DialogTitle>¿Desarmar {desarme?.nombre}?</DialogTitle>
            <DialogDescription>
              {desarme?.linea === null ? 'Sus productos quedan en el carrito como renglones sueltos' : 'Se quita ese producto y los demás quedan como renglones sueltos'}, sin el descuento del combo. El descuento solo vale si se vende el combo completo.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDesarme(null)}>Cancelar</Button>
            <Button onClick={confirmarDesarme} data-testid="confirmar-desarmar-combo">{desarme?.linea === null ? 'Desarmar' : 'Quitar y desarmar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={esperaAbierta} onOpenChange={setEsperaAbierta}>
        <DialogContent>
          <DialogHeader>
            <div className="flex items-center gap-1"><DialogTitle>Poner en espera (F9)</DialogTitle><AyudaVentana tema="pos.espera" /></div>
            <DialogDescription>No consume correlativo ni stock. Se descarta al terminar el día o al cerrar tu caja.</DialogDescription>
          </DialogHeader>
          <Campo etiqueta="Nombre / referencia" htmlFor="espera-nombre">
            <Input id="espera-nombre" autoFocus value={nombreEspera} onChange={(e) => setNombreEspera(e.target.value)} placeholder="Ej. Sr. Gómez, camioneta blanca" data-testid="espera-nombre" />
          </Campo>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEsperaAbierta(false)}>Cancelar</Button>
            <Button
              disabled={!nombreEspera.trim() || !entrada}
              cargando={guardarEspera.isPending}
              onClick={() =>
                entrada &&
                guardarEspera.mutate(
                  { nombre: nombreEspera.trim(), payload: entrada },
                  { onSuccess: () => { toast.success(`Espera "${nombreEspera.trim()}" guardada`); setEsperaAbierta(false); setNombreEspera(''); limpiar(); }, onError: (e) => toast.error(mensajeDeError(e)) },
                )
              }
              data-testid="espera-guardar"
            >
              Guardar espera
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={verEsperas} onOpenChange={setVerEsperas}>
        <DialogContent>
          <DialogHeader>
            <div className="flex items-center gap-1"><DialogTitle>Facturas en espera</DialogTitle><AyudaVentana tema="pos.espera" /></div>
            <DialogDescription>Al retomar, toma el siguiente correlativo del momento.</DialogDescription>
          </DialogHeader>
          {(esperas.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">No hay esperas.</p>
          ) : (
            <ul className="divide-y">
              {esperas.data!.map((e) => (
                <li key={e.id} className="flex items-center justify-between py-2 text-sm" data-testid={`espera-${e.nombre}`}>
                  <div>
                    <p className="font-medium">{e.nombre}</p>
                    <p className="text-[11px] text-muted-foreground">{e.cajero_nombre} · {formatearFechaHora(e.creado_en)} · {e.lineas_count} línea(s)</p>
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" onClick={() => retomarEspera(e)}>Retomar</Button>
                    <Button size="sm" variant="ghost" onClick={() => descartarEspera.mutate(e.id, { onSuccess: () => toast.success('Espera descartada') })}>Descartar</Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>

      {abonoAbierto && cliente ? (
        <CobroDialog
          abierto={abonoAbierto}
          cerrar={() => setAbonoAbierto(false)}
          ctx={{ modo: 'abono', titulo: `Abono / adelanto · ${cliente.razon_social}`, descripcion: 'Suma al saldo a favor del cliente. Solo en divisas; entra completo (el IGTF se cobra en la factura que se pague con este saldo). Recibo con correlativo.', porCobrar: '0', tasaBs: calculo?.tasas.tasa_paralela ?? '1', factorDivisa: '1', topeBs: null, saldoFavor: '0', parcial: true, etiquetaAccion: 'Registrar abono' }}
          alCobrar={(pagos) =>
            // La clave nace al abrir el diálogo: reintentar tras un error no registra otro abono
            abono.mutate(
              { datos: { cliente_id: cliente.id, pagos }, clave: claveAbono },
              { onSuccess: (r) => { mostrarRegistrado({ titulo: `Abono ${r.numero_formateado} registrado`, descripcion: `${cliente.razon_social} · saldo a favor $ ${Money.parse(r.saldo_a_favor).toDisplay()}`, imprimir: [{ etiqueta: 'Imprimir recibo', ruta: `/imprimir/ABONO/${r.id}` }], alCerrar: () => buscador.current?.focus() }); setAbonoAbierto(false); get<ClienteDTO>(`/clientes/${cliente.id}`).then(setCliente).catch(() => undefined); }, onError: (e) => toast.error(mensajeDeError(e)) },
            )
          }
          cargando={abono.isPending}
          ayuda={<AyudaVentana tema="pos.abono" />}
        />
      ) : null}

      <Dialog open={!!emitido} onOpenChange={(o) => !o && setEmitido(null)}>
        <DialogContent data-testid="dialogo-emitido" onCloseAutoFocus={(e) => { e.preventDefault(); buscador.current?.focus(); }}>
          <DialogHeader>
            <DialogTitle>
              {emitido?.tipo === 'FAC' ? 'Factura' : 'Nota'} {emitido?.numero_formateado} emitida
            </DialogTitle>
            <DialogDescription>{emitido?.control_formateado ? `N° de control ${emitido.control_formateado}` : 'Documento no fiscal'} · {emitido?.cliente_razon_social}</DialogDescription>
          </DialogHeader>
          {emitido ? (
            <div className="space-y-1 text-sm">
              {emitidoAntes ? (
                <p className="rounded-lg border border-mg-ambar bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-900" role="status" data-testid="emitido-intento-anterior">
                  Este documento quedó emitido en un intento anterior. El carrito que está en pantalla NO se ha emitido: revísalo y cobra solo lo que falte.
                </p>
              ) : null}
              <p>Total <b className="num">$ {Money.parse(emitido.total).toDisplay()}</b> · <span className="num">Bs {Money.parse(emitido.total_bs).toDisplay()}</span></p>
              {emitido.pagos[0] && (Money.parse(emitido.pagos[0].vuelto_usd).esPositivo() || Money.parse(emitido.pagos[0].vuelto_bs).esPositivo()) ? (
                <p className="text-mg-ambar">Vuelto: $ {Money.parse(emitido.pagos[0].vuelto_usd).toDisplay()} · Bs {Money.parse(emitido.pagos[0].vuelto_bs).toDisplay()}{emitido.pagos[0].vuelto_bs_metodo && emitido.pagos[0].vuelto_bs_metodo !== 'efectivo_bs' ? ` por ${METODO_VUELTO_BS_ETIQUETA[emitido.pagos[0].vuelto_bs_metodo].toLowerCase()}` : ''}</p>
              ) : null}
              {emitido.pagos[0] && Money.parse(emitido.pagos[0].sobrante_usd).esPositivo() ? <p className="text-xs text-muted-foreground" data-testid="emitido-sobrante">Sobrante por vuelto: $ {Money.parse(emitido.pagos[0].sobrante_usd).toDisplay()}</p> : null}
              {emitido.condicion === 'credito' ? <p className="text-mg-ambar">A crédito · vence {emitido.vence_en ? formatearFechaHora(emitido.vence_en) : ''}</p> : null}
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => emitido && window.open(`/imprimir/${emitido.tipo}/${emitido.id}`, '_blank')} data-testid="emitido-imprimir">
              <Printer /> Imprimir
            </Button>
            <Button variant="outline" onClick={() => emitido && navigate(`/documentos/${emitido.id}`)}>Ver documento</Button>
            <Button onClick={() => { setEmitido(null); buscador.current?.focus(); }} data-testid="emitido-nueva">Nueva venta</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Detalle de la venta para la pantalla de cobro, tal como lo calculó el servidor. */
function detalleCobro(c: CalculoVentaDTO, esFactura: boolean, conPrecioDivisa: boolean): DetalleCobroUI {
  const t = c.totales;
  const d = conPrecioDivisa ? t.divisa : null;
  return {
    esFactura,
    ivaPct: t.iva_pct,
    lineas: c.lineas.map((l, i) => ({
      codigo: l.producto_codigo,
      nombre: l.producto_nombre,
      unidades: l.unidades,
      unidad: l.producto_unidad,
      subtotal: l.subtotal,
      precioBs: l.precio_unitario_bs,
      bs: l.subtotal_bs,
      precioBsBcv: d?.lineas[i]?.precio_unitario_bs ?? null,
      bsBcv: d?.lineas[i]?.subtotal_bs ?? null,
    })),
    usd: { bruto: t.bruto, descuento: t.descuento, base: t.base, exento: t.exento, iva: t.iva, total: t.total, retencionIva: t.retencion_iva, retencionMunicipal: t.retencion_municipal, porCobrar: t.por_cobrar },
    bs: { bruto: t.bruto_bs, descuento: t.descuento_bs, base: t.base_bs, exento: t.exento_bs, iva: t.iva_bs, total: t.total_bs, retencionIva: t.retencion_iva_bs, retencionMunicipal: t.retencion_municipal_bs, porCobrar: t.por_cobrar_bs },
    bsBcv: d
      ? { bruto: d.bruto_bs, descuento: d.descuento_bs, base: d.base_bs, exento: d.exento_bs, iva: d.iva_bs, total: d.total_bs, retencionIva: d.retencion_iva_bs, retencionMunicipal: d.retencion_municipal_bs, porCobrar: d.por_cobrar_bs }
      : null,
  };
}
